// Base
import { useEffect, useRef, forwardRef } from "react";
import "intl-tel-input/build/css/intlTelInput.css";
import intlTelInput from "intl-tel-input";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";

// Types
import type { Ref } from "react";

// Store
import { useProductData } from "@/zustand/productStore";

interface PhoneFieldProps {
  handleChange: (value: string) => void;
  error?: string;
  value?: string;
  placeholder?: string;
  country?: string;
  contrast?: boolean;
}

const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(
  (
    { handleChange, error, value, placeholder, country, contrast },
    ref: Ref<HTMLInputElement>,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const itiRef = useRef<any>(null);
    const { country_code } = useProductData();

    useEffect(() => {
      if (!inputRef.current || !country_code) return;

      itiRef.current = intlTelInput(inputRef.current, {
        initialCountry: country_code.toLowerCase() || country,
        //@ts-ignore
        showSelectedDialCode: true,
        autoPlaceholder: "off",
        nationalMode: false,
        countrySearch: true,
        utilsScript:
          "https://cdn.jsdelivr.net/npm/intl-tel-input@18.5.3/build/js/utils.js",
      });

      const flagContainer = inputRef.current.parentElement?.querySelector(
        ".iti__flag-container",
      ) as HTMLElement | null;

      if (flagContainer) {
        flagContainer.style.borderRight = contrast
          ? "1px solid #A3A3A3"
          : "1px solid #E0E0E0";
      }

      const placeholderColor = contrast ? "#525252" : "#828282";
      const styleTag = document.createElement("style");
      styleTag.innerHTML = `
        #phone::placeholder {
          color: ${placeholderColor};
        }
      `;
      document.head.appendChild(styleTag);

      const handleChangeEvent = () => {
        if (!inputRef.current) return;

        const rawValue = inputRef.current.value;
        const selectedCountry = itiRef.current
          ?.getSelectedCountryData()
          ?.iso2?.toUpperCase();

        // Format number dynamically per country
        const formatted = new AsYouType(selectedCountry).input(rawValue);
        inputRef.current.value = formatted;

        const phoneNumber = parsePhoneNumberFromString(
          rawValue,
          selectedCountry,
        );
        handleChange(phoneNumber?.number || rawValue);
      };

      inputRef.current.addEventListener("input", handleChangeEvent);

      return () => {
        inputRef.current?.removeEventListener("input", handleChangeEvent);
        itiRef.current.destroy();
        styleTag.remove();
      };
    }, [country_code, handleChange, contrast, country]);

    if (!country_code) return null;

    return (
      <div className="relative flex flex-col gap-1">
        <label
          className={`text-sm font-bold whitespace-nowrap ${
            contrast ? "text-cc-dark-gray" : "text-cc-gray"
          }`}
        >
          {placeholder}
        </label>
        <div
          className={`relative flex items-center rounded-md border text-sm ${
            contrast ? "border-cc-gray-5" : "border-cc-gray-4"
          }`}
        >
          <input
            id="phone"
            placeholder={placeholder}
            type="tel"
            ref={(el) => {
              inputRef.current = el;
              if (typeof ref === "function") ref(el);
              else if (ref)
                (
                  ref as React.MutableRefObject<HTMLInputElement | null>
                ).current = el;
            }}
            defaultValue={value}
            autoComplete="tel"
            className="flex-1 p-2"
          />
        </div>
        {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
      </div>
    );
  },
);

export default PhoneField;
